import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, ChevronRight, BookOpen, Calendar, Clock, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Layout from '../components/layout/Layout';
import { departments, programmes, applicationFormUrl } from '../data/mock';

const DepartmentDetail = () => {
  const { id } = useParams();
  const department = departments.find((d) => d.id === id);
  const deptProgrammes = programmes.filter((p) => p.departmentId === id);

  if (!department) {
    return (
      <Layout>
        <div className="container-custom py-20 text-center">
          <h1 className="font-display text-3xl font-bold text-[#1e3a5f] mb-4">Department Not Found</h1>
          <Link to="/departments">
            <Button>Back to Departments</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${department.image})` }}
        >
          <div className="absolute inset-0 overlay-navy" />
        </div>
        <div className="container-custom relative z-10">
          <Link
            to="/departments"
            className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Departments
          </Link>
          <h1 className="font-display text-3xl md:text-5xl font-bold text-white mb-4">
            {department.name}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mb-4">
            {department.description}
          </p>
          <div className="flex gap-3">
            {department.intakes.map((intake) => (
              <Badge key={intake} className="bg-[#d4a855] text-[#142942] font-medium text-sm px-4 py-1">
                {intake} Intake
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Department Info */}
      <section className="section-padding">
        <div className="container-custom">
          {department.id === 'health-allied-sciences' && (
            <div className="bg-[#fef3c7] border border-[#d4a855] rounded-lg p-6 mb-10">
              <p className="text-[#92400e] flex items-center gap-3">
                <Calendar className="w-5 h-5 flex-shrink-0" />
                <span>
                  <strong>Important:</strong> All programmes under the Department of Health and Allied Sciences are available only during the <strong>September intake</strong>. Applications for the March intake are not accepted for these programmes.
                </span>
              </p>
            </div>
          )}

          <div className="mb-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-[#1e3a5f] mb-6">
              Programmes Offered
            </h2>
            <p className="text-[#475569] max-w-3xl">
              The {department.name} offers {deptProgrammes.length} comprehensive programme{deptProgrammes.length !== 1 ? 's' : ''} at NTA Level 4-6, designed to equip students with practical skills and theoretical knowledge for successful careers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {deptProgrammes.map((programme) => (
              <Link key={programme.id} to={`/programmes/${programme.id}`}>
                <Card className="h-full card-hover border-none shadow-md hover:shadow-xl cursor-pointer">
                  <CardHeader>
                    <div className="flex gap-2 mb-2">
                      {programme.ntaLevels.map((level) => (
                        <Badge key={level} variant="outline" className="text-xs border-[#1e3a5f] text-[#1e3a5f]">
                          NTA Level {level}
                        </Badge>
                      ))}
                    </div>
                    <CardTitle className="font-display text-[#1e3a5f] text-xl leading-tight">
                      {programme.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#475569] text-sm line-clamp-3 mb-4">
                      {programme.overview}
                    </p>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-[#475569]">
                        <Clock className="w-4 h-4 text-[#d4a855]" />
                        <span>{programme.duration}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#475569]">
                        <Calendar className="w-4 h-4 text-[#d4a855]" />
                        <span>Intakes: {programme.intakes.join(', ')}</span>
                      </div>
                    </div>
                    <div className="flex items-center text-[#2563eb] font-medium text-sm group">
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Career Pathways Overview */}
      <section className="section-padding bg-[#f8fafc]">
        <div className="container-custom">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[#1e3a5f] mb-8 text-center">
            Career Opportunities
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...new Set(deptProgrammes.flatMap((p) => p.careerPathways))].slice(0, 8).map((career, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-4 shadow-sm text-center"
              >
                <GraduationCap className="w-6 h-6 text-[#d4a855] mx-auto mb-2" />
                <p className="text-[#1e3a5f] text-sm font-medium">{career}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1e3a5f]">
        <div className="container-custom text-center">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Join {department.shortName}?
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-8">
            Take the first step towards your career. Apply now or contact us for more information.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={applicationFormUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#d4a855] hover:bg-[#f4d794] text-[#142942] font-semibold px-8">
                Apply Now
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
            <Link to="/admissions">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#1e3a5f] px-8">
                View Admissions Info
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default DepartmentDetail;
